import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Boxes, ChevronLeft, AlertOctagon, Info, Pencil, Trash2 } from "lucide-react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@monorepo-apps/ui/src/components/hover-card";

import { useEvaluationCriteria } from "@/application/use-cases/products/use-criteria.queries";
import { useProductFeatureCoverage } from "@/application/use-cases/products/use-feature-products.queries";
import { useProduct } from "@/application/use-cases/products/use-products.queries";
import {
  useProductScores,
  useUpsertProductScore,
} from "@/application/use-cases/products/use-scores.queries";
import { useDefaultWeightsPreset } from "@/application/use-cases/products/use-weights.queries";
import {
  computeDerivedCoverage,
  type DerivedCoverageResult,
} from "@/application/use-cases/products/derive-coverage";
import { computeProductScore } from "@/application/use-cases/products/compute-product-score";
import { useFeaturesList } from "@/application/use-cases/features/use-features.queries";
import { useAllVerdicts } from "@/application/use-cases/feature-roadmap-verdicts/use-verdicts.queries";
import { ContributionBar } from "@/presentation/components/products/contribution-bar";
import { DeleteProductDialog } from "@/presentation/components/products/delete-product-dialog";
import { DerivedScoreBadge } from "@/presentation/components/products/derived-score-badge";
import { EditProductDialog } from "@/presentation/components/products/edit-product-dialog";
import { EvidenceField } from "@/presentation/components/products/evidence-field";
import { ScorePicker } from "@/presentation/components/products/score-picker";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import {
  CRITERION_GROUP_LABEL,
  CRITERION_GROUP_ORDER,
  colorForGroup,
} from "@/presentation/data/criteria";
import type {
  CriterionGroup,
  EvaluationCriterionRow,
  FeatureProductRow,
  FeatureRoadmapVerdictRow,
  FeatureRow,
  ProductScoreRow,
  ScoreValue,
} from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/products/$slug")({
  loader: ({ params }) => {
    if (!params.slug) throw notFound();
    return null;
  },
  component: ProductDetailPage,
});

const PRODUCT_KIND_LABEL = {
  "off-the-shelf": "Off-the-shelf",
  "build-on-top": "Build on top",
  "from-scratch": "From scratch",
} as const;

function ProductDetailPage() {
  const { slug } = Route.useParams();
  const productQ = useProduct(slug);
  const criteriaQ = useEvaluationCriteria();
  const scoresQ = useProductScores(productQ.data?.id);
  const presetQ = useDefaultWeightsPreset();
  const featureProductsQ = useProductFeatureCoverage(productQ.data?.id);
  const featuresQ = useFeaturesList();
  const verdictsQ = useAllVerdicts();
  const upsertScore = useUpsertProductScore();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const criteria = criteriaQ.data ?? [];
  const scores = scoresQ.data ?? [];
  const product = productQ.data;
  const weights = presetQ.data?.weights ?? {};
  const productAssignments = featureProductsQ.data ?? [];
  const features = featuresQ.data ?? [];
  const verdicts = verdictsQ.data ?? [];

  const scoreByCriterion = useMemo(() => {
    const map = new Map<string, ProductScoreRow>();
    for (const s of scores) map.set(s.criterion_id, s);
    return map;
  }, [scores]);

  const derivedByCriterion = useMemo(() => {
    const map = new Map<string, DerivedCoverageResult>();
    for (const c of criteria) {
      if (c.derivation_kind !== "derived-coverage") continue;
      map.set(
        c.id,
        computeDerivedCoverage(c, {
          features,
          productAssignments,
          verdicts,
        })
      );
    }
    return map;
  }, [criteria, features, productAssignments, verdicts]);

  const totals = useMemo(
    () => computeTotals(criteria, scores, productAssignments, features, verdicts, weights),
    [criteria, scores, productAssignments, features, verdicts, weights]
  );

  if (productQ.isLoading || criteriaQ.isLoading) {
    return <LoadingState />;
  }
  if (!product) {
    return <EmptyState slug={slug} />;
  }

  const grouped = groupCriteria(criteria);

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-6xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <div className="flex items-center gap-2 text-[12px] print:hidden">
        <Link
          to="/products"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
        >
          <ChevronLeft className="size-3" />
          All products
        </Link>
      </div>

      <RouteHero
        eyebrow={`${PRODUCT_KIND_LABEL[product.kind]} · ${product.vendor ?? "—"}`}
        icon={Boxes}
        numeral={totals.weightedTotal.toFixed(2)}
        numeralTone={
          totals.dealbreakers > 0 ? "red" : totals.weightedTotal >= 3.5 ? "cyan" : "amber"
        }
        numeralSuffix="/ 5.00"
        title={product.name}
        summary={product.summary ?? undefined}
        collapsibleSummary={Boolean(product.summary && product.summary.length > 160)}
        meta={[
          { label: "Coverage", value: `${totals.coveragePct}%` },
          {
            label: "Evidence",
            value: `${totals.evidencePct}%`,
            tone: totals.vibesCount > 0 ? "amber" : "default",
          },
          {
            label: "Dealbreakers",
            value: totals.dealbreakers,
            tone: totals.dealbreakers > 0 ? "red" : "default",
          },
          { label: "Criteria", value: `${totals.scoredCount} / ${criteria.length}` },
        ]}
        actions={
          <div className="flex flex-col gap-2 print:hidden">
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="inline-flex w-fit items-center gap-1.5 rounded-md border border-[oklch(1_0_0_/_0.18)] bg-[oklch(1_0_0_/_0.04)] px-3 py-1.5 text-[11.5px] font-medium text-[oklch(0.97_0.005_240)] transition-colors hover:bg-[oklch(1_0_0_/_0.08)]"
            >
              <Pencil className="size-3" />
              Edit product
            </button>
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="inline-flex w-fit items-center gap-1.5 rounded-md border border-[oklch(0.78_0.18_25_/_0.40)] bg-[oklch(0.78_0.18_25_/_0.10)] px-3 py-1.5 text-[11.5px] font-medium text-[oklch(0.85_0.13_25)] transition-colors hover:bg-[oklch(0.78_0.18_25_/_0.20)]"
            >
              <Trash2 className="size-3" />
              Delete
            </button>
          </div>
        }
      />

      <EditProductDialog product={editOpen ? product : null} onClose={() => setEditOpen(false)} />
      <DeleteProductDialog
        product={deleteOpen ? product : null}
        onClose={() => setDeleteOpen(false)}
      />

      <p className="text-muted-foreground text-[12px] leading-relaxed">
        <span className="text-foreground/70 font-medium">How the score works:</span> the{" "}
        <span className="font-data">/ 5.00</span> total is the weighted sum of each criterion&apos;s
        0–5 score × its weight in the active preset (coverage criteria are auto-derived from the
        feature matrix); a dealbreaker fail drops the product below the line regardless of total.
      </p>

      {totals.dealbreakers > 0 ? (
        <div
          className="flex items-start gap-3 rounded-xl border p-4"
          style={{ borderColor: "oklch(0.78 0.18 25 / 0.40)", background: "oklch(0.97 0.04 25)" }}
        >
          <AlertOctagon
            className="mt-0.5 size-4 shrink-0"
            style={{ color: "oklch(0.55 0.20 25)" }}
          />
          <div className="flex flex-col gap-1 text-[13px]">
            <span className="font-medium" style={{ color: "oklch(0.45 0.18 25)" }}>
              Below the line — {totals.dealbreakers} dealbreaker{" "}
              {totals.dealbreakers === 1 ? "fail" : "fails"} recorded
            </span>
            <span className="text-foreground/70">
              Resolve the failing criteria or document a waiver to bring this product back into
              ranking.
            </span>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-7">
        {CRITERION_GROUP_ORDER.map((group) => {
          const rows = grouped.get(group);
          if (!rows || rows.length === 0) return null;
          return (
            <section key={group} className="flex flex-col gap-3">
              <h2 className="eyebrow flex items-center gap-2">
                <span
                  className="inline-block size-1.5 rounded-full"
                  style={{ background: colorForGroup(group) }}
                />
                {CRITERION_GROUP_LABEL[group]}
              </h2>
              <div className="glow-card divide-border/60 divide-y overflow-hidden">
                <CriterionTableHeader />
                {rows.map((c) => {
                  const s = scoreByCriterion.get(c.id) ?? null;
                  const weight = weights[c.slug] ?? c.weight_default;
                  const derived = derivedByCriterion.get(c.id) ?? null;
                  return (
                    <CriterionRow
                      key={c.id}
                      criterion={c}
                      score={s}
                      weight={weight}
                      productSlug={product.slug}
                      derived={derived}
                      onUpdate={(next) => {
                        upsertScore.mutate({
                          product_id: product.id,
                          criterion_id: c.id,
                          score: next.score,
                          dealbreaker_fail: next.dealbreakerFail,
                          evidence_md: next.evidence ?? null,
                          vibes_revisit: next.vibesRevisit,
                        });
                      }}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function CriterionTableHeader() {
  return (
    <div className="text-muted-foreground bg-muted/30 grid grid-cols-[1fr_60px_220px_120px_1fr] items-center gap-3 px-4 py-2 text-[10.5px] font-medium tracking-[0.12em] uppercase">
      <span>Criterion</span>
      <span className="text-right">Weight</span>
      <span>Score</span>
      <span>Contribution</span>
      <span>Evidence</span>
    </div>
  );
}

function CriterionRow({
  criterion,
  score,
  weight,
  productSlug,
  derived,
  onUpdate,
}: {
  criterion: EvaluationCriterionRow;
  score: ProductScoreRow | null;
  weight: number;
  productSlug: string;
  derived: DerivedCoverageResult | null;
  onUpdate: (next: {
    score: ScoreValue | null;
    dealbreakerFail: boolean;
    evidence: string | null;
    vibesRevisit: boolean;
  }) => void;
}) {
  const accent = colorForGroup(criterion.group_key);
  const isDerived = criterion.derivation_kind === "derived-coverage";

  // For derived criteria, the score comes from the computed result, not product_scores
  const value = isDerived ? null : (score?.score ?? null);
  const derivedScore = derived?.score ?? null;
  const dealbreakerFail = score?.dealbreaker_fail ?? false;
  const evidence = score?.evidence_md ?? null;
  const vibes = score?.vibes_revisit ?? false;

  const effectiveScore = isDerived ? derivedScore : value;
  const contribution = effectiveScore !== null && !dealbreakerFail ? effectiveScore * weight : 0;

  const dealbreakerRowTint = dealbreakerFail ? { background: "oklch(0.97 0.04 25 / 0.55)" } : {};

  return (
    <div
      className="grid grid-cols-[1fr_60px_220px_120px_1fr] items-start gap-3 px-4 py-3 print:break-inside-avoid"
      style={dealbreakerRowTint}
    >
      <div className="flex flex-col gap-0.5">
        <span className="flex items-center gap-1.5 text-[13px] leading-tight font-medium">
          {criterion.name}
          {criterion.rubric ? <RubricHint rubric={criterion.rubric} /> : null}
        </span>
        {criterion.description ? (
          <span className="text-foreground/60 line-clamp-2 text-[11.5px] leading-snug print:line-clamp-none">
            {criterion.description}
          </span>
        ) : null}
        {criterion.is_dealbreaker_eligible ? (
          <span
            className="font-data mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-[oklch(0.97_0.04_25)] px-1.5 py-0.5 text-[9.5px] tracking-[0.10em] uppercase"
            style={{ color: "oklch(0.55 0.20 25)" }}
          >
            dealbreaker eligible
          </span>
        ) : null}
      </div>

      <div className="font-data text-foreground/70 pt-1 text-right text-[12px] tabular-nums">
        {(weight * 100).toFixed(0)}%
      </div>

      <div className="pt-0.5">
        {isDerived ? (
          <DerivedScoreBadge
            result={derived ?? emptyDerived()}
            accentColor={accent}
            matrixLinkSearch={{ product: productSlug }}
          />
        ) : (
          <ScorePicker
            value={value}
            dealbreakerFail={dealbreakerFail}
            dealbreakerEligible={criterion.is_dealbreaker_eligible}
            accentColor={accent}
            onChange={(next) =>
              onUpdate({
                score: next.score,
                dealbreakerFail: next.dealbreakerFail,
                evidence: evidence,
                vibesRevisit:
                  vibes && next.score === value && next.dealbreakerFail === dealbreakerFail
                    ? vibes
                    : evidence === null && next.score !== null,
              })
            }
          />
        )}
      </div>

      <div className="flex flex-col gap-1.5 pt-1">
        <span className="font-data text-[12px] tabular-nums">
          {effectiveScore !== null && !dealbreakerFail
            ? contribution.toFixed(2)
            : dealbreakerFail
              ? "fail"
              : "—"}
        </span>
        <ContributionBar
          segments={
            effectiveScore !== null && !dealbreakerFail
              ? [
                  {
                    slug: criterion.slug,
                    label: criterion.name,
                    color: accent,
                    value: contribution,
                  },
                ]
              : []
          }
          maxTotal={Math.max(0.5, weight * 5)}
          height={6}
        />
      </div>

      <div className="pt-0.5">
        {isDerived ? (
          <span className="text-foreground/50 text-[11.5px] italic">
            Evidence lives on the matrix page per assignment.
          </span>
        ) : (
          <EvidenceField
            value={evidence}
            vibesRevisit={vibes}
            placeholder="Quote, link, or interview reference…"
            onSave={({ evidence: next, vibesRevisit: nextVibes }) =>
              onUpdate({
                score: value,
                dealbreakerFail,
                evidence: next,
                vibesRevisit: nextVibes,
              })
            }
            onAcceptVibes={() =>
              onUpdate({
                score: value,
                dealbreakerFail,
                evidence: evidence,
                vibesRevisit: false,
              })
            }
          />
        )}
      </div>
    </div>
  );
}

function RubricHint({ rubric }: { rubric: string }) {
  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label="Show scoring rubric"
          className="text-foreground/40 hover:text-foreground/80 inline-flex shrink-0 cursor-help transition-colors print:hidden"
        >
          <Info className="size-3.5" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-[420px] p-3">
        <div className="eyebrow mb-2">Scoring rubric</div>
        <pre className="font-data text-foreground/80 max-h-[360px] overflow-auto text-[11.5px] leading-relaxed whitespace-pre-wrap">
          {rubric}
        </pre>
      </HoverCardContent>
    </HoverCard>
  );
}

function emptyDerived(): DerivedCoverageResult {
  return {
    score: null,
    ratio: null,
    inScopeCount: 0,
    nativeCount: 0,
    pluginCount: 0,
    customCount: 0,
    weightedSum: 0,
  };
}

function LoadingState() {
  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-6xl flex-col gap-8 p-6 lg:p-10">
      <div className="bg-muted/40 h-48 animate-pulse rounded-2xl" />
      <div className="bg-muted/30 h-64 animate-pulse rounded-2xl" />
    </div>
  );
}

function EmptyState({ slug }: { slug: string }) {
  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-3xl flex-col gap-4 p-10 text-center">
      <h1 className="text-xl font-semibold">Product not found</h1>
      <p className="text-muted-foreground text-sm">
        No product with slug <code className="font-data">{slug}</code>. It may have been removed or
        never seeded.
      </p>
      <Link to="/products" className="text-primary hover:underline">
        ← Back to products
      </Link>
    </div>
  );
}

interface Totals {
  weightedTotal: number;
  coveragePct: number;
  evidencePct: number;
  vibesCount: number;
  dealbreakers: number;
  scoredCount: number;
}

function computeTotals(
  criteria: EvaluationCriterionRow[],
  scores: ProductScoreRow[],
  productAssignments: FeatureProductRow[],
  features: FeatureRow[],
  verdicts: FeatureRoadmapVerdictRow[],
  weights: Record<string, number>
): Totals {
  const result = computeProductScore(scores, productAssignments, {
    criteria,
    features,
    verdicts,
    weights,
  });
  // Coverage % is based on the manually-scored criteria only (derived ones are
  // computed automatically — counting them in evidence/coverage stats would be
  // confusing).
  const manualCount = criteria.filter((c) => c.derivation_kind === "manual").length;
  return {
    weightedTotal: result.weightedTotal,
    coveragePct: criteria.length > 0 ? Math.round((result.scoredCount / criteria.length) * 100) : 0,
    evidencePct: manualCount > 0 ? Math.round((result.withEvidenceCount / manualCount) * 100) : 0,
    vibesCount: result.vibesCount,
    dealbreakers: result.dealbreakerNames.length,
    scoredCount: result.scoredCount,
  };
}

function groupCriteria(criteria: EvaluationCriterionRow[]) {
  const map = new Map<CriterionGroup, EvaluationCriterionRow[]>();
  for (const c of criteria) {
    const bucket = map.get(c.group_key) ?? [];
    bucket.push(c);
    map.set(c.group_key, bucket);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => a.display_order - b.display_order);
  }
  return map;
}
